import { componentSizes } from '@/theme/componentSizes';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { type SpacingToken } from '@/theme/tokens';
import { s } from '@/theme/tokens';
import { Box, type SxProps, type Theme, useTheme } from '@/ui/primitives';
import { getChipPalette, getStatusChipKey } from '@/ui/primitives';
import { StatCard } from './StatCard';
import type { StatusChipStatus } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type StatusOption<TStatus extends StatusChipStatus> = { value: TStatus; label: string };

export type SyncReactNode = Exclude<ReactNode, Promise<unknown>>;

type StatusCountCardsProps<TStatus extends StatusChipStatus> = {
  statusOptions: Array<StatusOption<TStatus>>;
  counts: Partial<Record<TStatus, number | null | undefined>>;
  loading?: boolean;
  loadingValueWidth?: number;
  totalLabel?: string;
  activeStatuses?: TStatus[];
  onStatusSelect?: (status: TStatus) => void;
  onTotalSelect?: () => void;
  formatValue?: (value: number | null | undefined, status: TStatus) => SyncReactNode;
  formatTotalValue?: (value: number | null | undefined) => SyncReactNode;
  minColumnWidth?: number;
  gap?: SpacingToken;
  cardSx?: SxProps<Theme>;
};

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const defaultFormatValue = (value: number | null | undefined): SyncReactNode => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString();
};

export const StatusCountCards = <TStatus extends StatusChipStatus>({
  statusOptions,
  counts,
  loading = false,
  loadingValueWidth = 25,
  totalLabel = 'Total',
  activeStatuses,
  onStatusSelect,
  onTotalSelect,
  formatValue = defaultFormatValue,
  formatTotalValue = defaultFormatValue,
  minColumnWidth = componentSizes.cards.statusCountMinWidth,
  gap = 'md',
  cardSx,
}: StatusCountCardsProps<TStatus>): ReactElement => {
  const theme = useTheme();
  const borderColor = resolveSemanticColor(theme, 'border');
  const accentColor = resolveSemanticColor(theme, 'accent');
  const activeSet = new Set(activeStatuses ?? []);
  const totalValue = statusOptions.reduce((sum, option) => sum + (counts[option.value] ?? 0), 0);
  const resolvedFormatValue = loading
    ? () => null
    : (value: number | null | undefined, status: TStatus) => formatValue(value, status);
  const resolvedFormatTotalValue = loading
    ? () => null
    : (value: number | null | undefined) => formatTotalValue(value);
  const gapValue = SPACING_MAP[gap];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`,
        gap: gapValue,
      }}
    >
      <StatCard
        key="total"
        title={totalLabel}
        value={resolvedFormatTotalValue(totalValue)}
        loading={loading}
        loadingWidth={loadingValueWidth}
        accentColor={accentColor}
        onClick={onTotalSelect}
        cardSx={
          [
            { border: '1px solid', borderColor },
            ...(Array.isArray(cardSx) ? cardSx : cardSx ? [cardSx] : []),
          ] as SxProps<Theme>
        }
      />
      {statusOptions.map((option) => {
        const palette = getChipPalette(getStatusChipKey(option.value));
        const value = counts[option.value];
        const isActive = activeSet.has(option.value);
        const baseCardSx: SxProps<Theme> = {
          border: '1px solid',
          borderColor: isActive ? palette.border : borderColor,
        };
        const resolvedCardSx = cardSx ? ([baseCardSx, cardSx] as SxProps<Theme>) : baseCardSx;
        return (
          <StatCard
            key={option.value}
            title={option.label}
            value={resolvedFormatValue(value, option.value)}
            loading={loading}
            loadingWidth={loadingValueWidth}
            accentColor={palette.icon}
            activeBorderColor={palette.border}
            isActive={isActive}
            onClick={
              onStatusSelect
                ? () => {
                    onStatusSelect(option.value);
                  }
                : undefined
            }
            cardSx={resolvedCardSx}
          />
        );
      })}
    </Box>
  );
};
